/**
 * Employee Lifeline -- the pure part: scales, lanes, stations, finding markers, weeks.
 * No React and no DOM, so tests/test_employee_lifeline_frontend.py runs it under node.
 *
 * Every chart has ONE scale per axis. The map's time axis is one monotonic, piecewise-
 * linear scale: the before-hire zone is magnified on purpose (the design's "map, not a
 * precise chart") and labelled so; employment runs linearly from hire to today.
 */

const DAY = 86400000

// 'YYYY-MM-DD' -> ms (UTC); null for anything else.
export function dayMs(s) {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(s)) return null
  const [y, m, d] = s.slice(0, 10).split('-').map(Number)
  return Date.UTC(y, m - 1, d)
}

export function isoDay(ms) {
  return new Date(ms).toISOString().slice(0, 10)
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function fmtDate(s, withYear = true) {
  const ms = dayMs(s)
  if (ms == null) return '—'
  const d = new Date(ms)
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}${withYear ? ` ${d.getUTCFullYear()}` : ''}`
}

export function fmtMoney(n, digits = 2) {
  if (n == null || Number.isNaN(Number(n))) return '—'
  return `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`
}

export function fmtHours(n) {
  if (n == null || Number.isNaN(Number(n))) return '—'
  return `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })} h`
}

export function tenure(fromDay, toDay) {
  const a = dayMs(fromDay), b = dayMs(toDay)
  if (a == null || b == null || b < a) return null
  const months = Math.floor((b - a) / (DAY * 30.4375))
  return `${Math.floor(months / 12)} y ${months % 12} m`
}

// The lines, in map order, with the canvas's colours (scoped CSS variables in Lifeline.css).
// `help` is the line's meaning in plain words: the chip's tooltip and the "How to read this" key.
export const LINES = [
  { key: 'job', label: 'Job', help: 'job title and code over time' },
  { key: 'pay', label: 'Pay', help: 'base pay rate over time; a step is a pay change' },
  { key: 'manager', label: 'Manager', help: 'who this person reported to' },
  { key: 'location', label: 'Location', help: 'the work location' },
  { key: 'status', label: 'Status', help: 'active, on leave (hatched) or terminated (dotted)' },
  { key: 'onboarding', label: 'Onboarding', help: 'the new-hire tasks after the hire, from the onboarding record' },
]

/**
 * Pay basis: how a pay segment (or person) is paid, from UKG's own salaried/hourly flag
 * (`pay_type`, read from salaryOrHourly). Never decided from the amount. Without a flag,
 * `fallback` (the person's), else the one figure the segment carries; a salaried record
 * also carries an hourly equivalent, so "has an hourly rate" never means hourly.
 */
export function payBasis(seg, fallback = null) {
  if (seg?.pay_type === 'salaried' || seg?.pay_type === 'hourly') return seg.pay_type
  if (fallback === 'salaried' || fallback === 'hourly') return fallback
  if (seg?.hourly_rate) return 'hourly'
  if (seg?.annual_salary) return 'salaried'
  return null
}

// A pay segment in its own terms: a salary per year, a wage per hour.
export function payText(seg, fallback = null) {
  if (!seg) return ''
  const yr = seg.annual_salary ? `${fmtMoney(seg.annual_salary, 0)}/yr` : ''
  const hr = seg.hourly_rate ? `${fmtMoney(seg.hourly_rate)}/h` : ''
  return payBasis(seg, fallback) === 'salaried' ? (yr || hr) : (hr || yr)
}

// The figure the Pay line and the strata draw, on one scale: the current basis decides it
// (an annual salary for a salaried person, the hourly rate for an hourly one).
export function payMetric(segs, person = null) {
  const list = segs || []
  const last = list[list.length - 1]
  const want = payBasis(last, person?.pay_type) === 'salaried' ? 'annual_salary' : 'hourly_rate'
  const other = want === 'annual_salary' ? 'hourly_rate' : 'annual_salary'
  return list.some(s => s[want]) || !list.some(s => s[other]) ? want : other
}

// A figure in a metric's terms.
export function fmtPay(v, metric) {
  if (v == null) return '—'
  return metric === 'annual_salary' ? `${fmtMoney(v, 0)}/yr` : `${fmtMoney(v)}/h`
}

// The view tabs: Story is on every screen; the map and the Table need the width.
export function viewTabs(phone) {
  return phone
    ? [['map', 'Story'], ['pay', 'Pay'], ['patterns', 'Patterns']]
    : [['map', 'Map'], ['story', 'Story'], ['table', 'Table'], ['pay', 'Pay strata'], ['patterns', 'Patterns']]
}

export function segmentLabel(line, seg) {
  if (!seg) return ''
  switch (line) {
    case 'job': return [seg.job_title, seg.job_code].filter(Boolean).join(' · ')
    case 'pay': return payText(seg)
    case 'manager': return seg.name || 'No supervisor on record'
    case 'location': return [seg.description || seg.code, seg.state].filter(Boolean).join(' · ')
    case 'status': return seg.label || seg.code || ''
    default: return ''
  }
}

/**
 * The map's time scale. `start` = the left edge (the earliest thing drawn), `hire` = where
 * employment begins, `end` = today. The before-hire span gets `preWidth` pixels whatever its
 * length; employment gets the rest, linearly. Returns x(day) plus its pieces for labels.
 */
export function timeScale({ start, hire, end, left, right, preWidth }) {
  const s = dayMs(start), h = dayMs(hire), e = dayMs(end)
  const hasPre = s != null && h != null && s < h && preWidth > 0
  const empLeft = hasPre ? left + preWidth : left
  const empStart = h != null ? h : s
  const span = Math.max(DAY, (e ?? empStart + DAY) - empStart)
  const x = (day) => {
    const t = typeof day === 'number' ? day : dayMs(day)
    if (t == null) return null
    if (hasPre && t < h) return left + Math.max(0, (t - s) / (h - s)) * preWidth
    return empLeft + Math.min(1, Math.max(0, (t - empStart) / span)) * (right - empLeft)
  }
  const years = []
  if (empStart != null && e != null) {
    for (let y = new Date(empStart).getUTCFullYear() + 1; y <= new Date(e).getUTCFullYear(); y++) {
      years.push({ label: String(y), x: x(Date.UTC(y, 0, 1)) })
    }
  }
  return { x, left, right, empLeft, hasPre, years }
}

// The first day anything happened: hire, else the first segment or check.
export function firstDay(data) {
  const p = data?.person || {}
  const cands = [p.last_hire_date, p.original_hire_date]
  const s = data?.sections || {}
  for (const k of ['job', 'pay', 'status']) {
    const segs = s[k]?.segments
    if (segs?.length) cands.push(segs[0].start)
  }
  const checks = s.pay_statements?.checks
  if (checks?.length) cands.push(checks[0].pay_date)
  const ms = cands.map(dayMs).filter(v => v != null)
  return ms.length ? isoDay(Math.min(...ms)) : null
}

/**
 * A finding's place on the timeline: the date its evidence names (pay date first, then work
 * date) and, for a pay date, the check it points at (same day, else the nearest).
 */
export function findingMarkers(findings, checks) {
  const out = []
  const list = Array.isArray(checks) ? checks : []
  for (const f of findings || []) {
    const ev = (f.evidence || [])[0] || {}
    const day = ev.pay_date || ev.work_date || null
    let check = null
    if (ev.pay_identifier) check = list.find(c => c.pay_identifier === ev.pay_identifier) || null
    if (!check && day && list.length) {
      const t = dayMs(day)
      // a work date points at the first check paid on or after it; a pay date at the nearest
      if (!ev.pay_date && ev.work_date) {
        check = list.find(c => dayMs(c.pay_date) >= t) || null
      } else {
        check = list.reduce((best, c) => (best == null || Math.abs(dayMs(c.pay_date) - t) < Math.abs(dayMs(best.pay_date) - t) ? c : best), null)
      }
    }
    out.push({ id: f.observation_id, finding: f, day, check, severity: severityTier(f.severity) })
  }
  return out
}

export function severityTier(sev) {
  const s = String(sev || '').toLowerCase()
  if (['critical', 'high', 'error', 'risk'].includes(s)) return 'high'
  if (['warning', 'medium', 'insight'].includes(s)) return 'medium'
  return 'low'
}

// Events -> stations: which lines each touches, and whether it is an interchange.
export function stations(events) {
  return (events || []).map((e, i) => {
    const lines = [...new Set((e.changes || []).map(c => c.line))]
    if (!lines.length && (e.kinds || []).includes('hire')) lines.push('job', 'pay', 'manager', 'location', 'status')
    return { id: `ev-${i}`, ...e, lines, title: eventTitle(e) }
  })
}

// Neutral fallbacks, used only when UKG's own reason description isn't available. Nothing
// here infers a promotion or a transfer: those come from UKG's reason, or not at all.
const KIND_LABEL = {
  hire: 'Hired', rehire: 'Rehired', leave: 'Leave began', return: 'Returned from leave',
  termination: 'Terminated', status_change: 'Status change', job_change: 'Job change',
  rate_change: 'Pay change', manager_change: 'Manager change', location_change: 'Location change',
}

export function eventTitle(e) {
  if (e.reason) return e.reason                              // UKG's description of the reason code
  const k = (e.kinds || []).find(x => KIND_LABEL[x])
  return KIND_LABEL[k] || 'Change'
}

// What happened, in words, for the Table: the status event (hired, rehired, on leave ...)
// and the lines that changed ("Job and manager change"). UKG's reason is its own column.
const EVENT_LABEL = { hire: 'Hired', rehire: 'Rehired', leave: 'Leave began', return: 'Returned from leave',
                      termination: 'Terminated', status_change: 'Status change' }
const LINE_KIND = { job_change: 'job', rate_change: 'pay', manager_change: 'manager', location_change: 'location' }

export function changeSummary(e) {
  const kinds = e?.kinds || []
  const events = kinds.map(k => EVENT_LABEL[k]).filter(Boolean)
  const lines = kinds.map(k => LINE_KIND[k]).filter(Boolean)
  const list = lines.length < 2 ? lines.join('') : `${lines.slice(0, -1).join(', ')} and ${lines[lines.length - 1]}`
  const what = list ? `${list} change` : ''
  if (!events.length) return what ? what[0].toUpperCase() + what.slice(1) : 'Change'
  return [...events, what].filter(Boolean).join(' · ')
}

// The lines a row marks with a dot: the lines that changed, in map order (a hire starts them all).
export function rowLines(st) {
  const set = new Set(st?.lines || [])
  return LINES.map(l => l.key).filter(k => set.has(k))
}

// The findings that name a row: a finding whose evidence date, or the date of the check it
// points at, is the row's date. An undated finding names no row.
export function findingsOn(markers, day) {
  const t = dayMs(day)
  if (t == null) return []
  return (markers || []).filter(m => dayMs(m.day) === t || (m.check && dayMs(m.check.pay_date) === t))
}

export function findingHelp(m) {
  const sev = m.severity === 'high' ? 'High' : m.severity === 'medium' ? 'Medium' : 'Low'
  return `${sev} finding: ${m.finding?.title || 'finding'}.${m.check ? ` It points at the check paid ${fmtDate(m.check.pay_date)}.` : ''} Click to see the evidence.`
}

export function changeText(c) {
  return `${LINES.find(l => l.key === c.line)?.label || c.line}: ${segmentLabel(c.line, c.from) || '—'} → ${segmentLabel(c.line, c.to) || '—'}`
}

/**
 * Weeks for the year spiral: one bead per week from the first day to today, with hours from
 * WFM days where they exist, else each check's hours spread over its period. Location state
 * comes from the location line.
 */
export function weeks(data) {
  const s = data?.sections || {}
  const start = dayMs(firstDay(data))
  const end = dayMs(data?.as_of)
  if (start == null || end == null || end < start) return []
  const monday = start - ((new Date(start).getUTCDay() + 6) % 7) * DAY
  const wfmHours = new Map()
  const hours = new Map()
  const wfmDays = s.wfm_daily?.available ? s.wfm_daily.days : []
  const wfmFrom = wfmDays.length ? dayMs(wfmDays[0].date) : null
  const wfmTo = wfmDays.length ? dayMs(wfmDays[wfmDays.length - 1].date) : null
  for (const d of wfmDays) {
    const w = Math.floor((dayMs(d.date) - monday) / (7 * DAY))
    wfmHours.set(w, (wfmHours.get(w) || 0) + (d.hours || 0))
  }
  const source = wfmDays.length ? 'wfm' : (s.pay_statements?.available ? 'checks' : null)
  if (s.pay_statements?.available) {
    for (const c of s.pay_statements.checks) {
      const a = dayMs(c.period_start) ?? dayMs(c.pay_date), b = dayMs(c.period_end) ?? a
      if (a == null || c.hours == null) continue
      const n = Math.max(1, Math.round((b - a) / DAY) + 1)
      for (let i = 0; i < n; i++) {
        const w = Math.floor((a + i * DAY - monday) / (7 * DAY))
        hours.set(w, (hours.get(w) || 0) + c.hours / n)
      }
    }
  }
  const locs = s.location?.segments || []
  const stat = s.status?.segments || []
  const at = (segs, t) => segs.find(g => dayMs(g.start) <= t && (g.end == null || dayMs(g.end) > t))
  const out = []
  for (let t = monday, i = 0; t <= end; t += 7 * DAY, i++) {
    const loc = at(locs, t + 3 * DAY)
    const st = at(stat, t + 3 * DAY)
    // WFM where it covers the week (timecards are the hours worked), else the checks' hours
    const covered = wfmFrom != null && t + 6 * DAY >= wfmFrom && t <= wfmTo
    const h = covered ? wfmHours.get(i) : hours.get(i)
    out.push({ i, start: isoDay(t), hours: source ? Math.round((h || 0) * 10) / 10 : null,
               from: covered ? 'wfm' : (s.pay_statements?.available ? 'checks' : null),
               state: loc?.state || loc?.code || null, status: st?.code || null })
  }
  return out
}

// Pay strata: one stripe per check; above the axis what was earned, below where it went.
export function strata(checks) {
  return (checks || []).map(c => {
    const split = c.regular_amount != null
    return {
      id: c.pay_identifier, day: c.pay_date,
      up: split
        ? [{ k: 'regular', v: Math.max(0, c.regular_amount) }, { k: 'overtime', v: Math.max(0, c.overtime_amount || 0) },
           { k: 'other', v: Math.max(0, c.other_amount || 0) }]
        : [{ k: 'gross', v: Math.max(0, c.gross || 0) }],
      down: [{ k: 'net', v: Math.max(0, c.net || 0) }, { k: 'deductions', v: Math.max(0, c.deductions || 0) },
             { k: 'taxes', v: Math.max(0, c.taxes || 0) }],
      check: c,
    }
  })
}

// A linear scale (one per axis): domain [d0, d1] -> range [r0, r1].
export function linear(d0, d1, r0, r1) {
  const span = d1 - d0 || 1
  return v => r0 + ((v - d0) / span) * (r1 - r0)
}

/**
 * The before-hire journey (sections.pre_hire, phase 2). Every part is either available or
 * says why not; these helpers read only the available parts.
 */
export function preHireParts(pre) {
  // the section (available: true) or one of its hires (no flag of its own)
  if (!pre || pre.available === false) return null
  const part = k => (pre[k]?.available ? pre[k] : null)
  return { requisition: part('requisition'), application: part('application'), offer: part('offer'), onboarding: part('onboarding') }
}

export function fmtDays(n) {
  if (n == null || Number.isNaN(Number(n))) return '—'
  const v = Number(n)
  return `${Number.isInteger(v) ? v : v.toFixed(1)} d`
}

// One bar per workflow step: from the day it was entered to the day it was left (the step
// it stands in now runs to the hire date). `slow` = more than the peer median.
export function stepBars(pre) {
  const p = preHireParts(pre)
  const steps = p?.application?.steps || []
  return steps.map((st, i) => ({
    i, name: st.name, from: st.entered_on, to: st.left_on || pre.hire_date || st.entered_on,
    days: st.days, median: st.median_days, open: !st.left_on,
    slow: st.days != null && st.median_days != null && st.days > st.median_days,
  })).filter(b => dayMs(b.from) != null)
}

// The offer against the first paycheck, in words, or null when it can't be compared.
export function offerCheckText(offer) {
  const v = offer?.vs_first_check
  if (!v?.available) return null
  const money = v.compared_with === 'hr_annual_salary_at_hire' ? (x => fmtMoney(x, 0)) : fmtMoney
  const what = v.compared_with === 'hr_annual_salary_at_hire' ? 'HR annual salary at hire' : 'first paycheck rate'
  if (!v.differs) return `Offer ${money(v.offered)} matches the ${what}.`
  const dir = v.difference > 0 ? 'more' : 'less'
  return `Offer ${money(v.offered)} ≠ ${what} ${money(v.paid)} (${money(Math.abs(v.difference))} ${dir}).`
}

// The before-hire journey as dated rows, oldest first: requisition opened, applied, each
// step moved into, offer sent and accepted, onboarding started and completed, hired.
export function preHireEvents(pre) {
  const p = preHireParts(pre)
  if (!p) return []
  const out = []
  const add = (date, title, detail, line = null) => { if (dayMs(date) != null) out.push({ date, title, detail: detail || null, kind: 'pre', line }) }
  const rq = p.requisition, app = p.application, offer = p.offer, onb = p.onboarding
  if (rq) add(rq.opened_on, `Requisition ${rq.number || ''} opened`.replace(/\s+/g, ' ').trim(), rq.title)
  if (app) {
    add(app.applied_on, 'Applied', [app.source, app.internal ? 'internal applicant' : null].filter(Boolean).join(' · '))
    const steps = app.steps || []
    for (let i = 1; i < steps.length; i++) {
      const prev = steps[i - 1]
      add(steps[i].entered_on, `Moved to ${steps[i].name}`,
          `${prev.name}: ${fmtDays(prev.days)}${prev.median_days != null ? ` (median ${fmtDays(prev.median_days)})` : ''}`)
    }
  }
  if (offer) {
    add(offer.sent_on, 'Offer sent', offer.rate != null ? `${offer.rate_basis === 'annual' ? fmtMoney(offer.rate, 0) + '/yr' : fmtMoney(offer.rate) + '/h'}` : null)
    add(offer.accepted_on, 'Offer accepted', offerCheckText(offer))
  }
  if (onb) {
    add(onb.started_on, 'Onboarding started', onb.tasks_total != null ? `${onb.tasks_done} of ${onb.tasks_total} tasks done` : null, 'onboarding')
    add(onb.completed_on, 'Onboarding completed', null, 'onboarding')
  }
  add(pre.hire_date, 'Hired (recruiting)', app?.days_to_hire != null ? `${fmtDays(app.days_to_hire)} from applying` : null)
  return out.map((e, i) => ({ ...e, id: `pre-${i}` })).sort((a, b) => dayMs(a.date) - dayMs(b.date))
}

// ── label layout: nothing drawn on top of anything else ────────────────────────────
// SVG text can't be measured before it is drawn, so widths are estimated from the font
// size (a generous average glyph width: an estimate that errs wide never overlaps).
export const GLYPH = 0.62

export function textWidth(s, px = 11) {
  return String(s ?? '').length * px * GLYPH
}

// `s` cut to fit `maxW` px with an ellipsis; '' when not even a few characters fit.
export function fitText(s, maxW, px = 11, minChars = 4) {
  const str = String(s ?? '')
  if (textWidth(str, px) <= maxW) return str
  const n = Math.floor(maxW / (px * GLYPH)) - 1
  return n >= minChars ? `${str.slice(0, n).trimEnd()}…` : ''
}

/**
 * Labels for consecutive segments on one lane: each starts `pad` px after its segment's
 * start and may run only to the next segment's start (or `right`), so two labels on a
 * lane can never meet. Stops = x positions of anything else drawn across a label's row
 * (an interchange that spans several lanes), for the whole lane (`stops`) or one segment
 * (`g.stops`): a label also ends before the first one after it. Returns, per segment,
 * { x, text, full } (text '' = not drawn).
 */
export function laneLabels(segs, { pad = 8, right, px = 11, gap = 6, stops = [] }) {
  return segs.map((g, i) => {
    const x = g.x + pad
    const next = i + 1 < segs.length ? segs[i + 1].x : right
    const stop = Math.min(next, ...[...stops, ...(g.stops || [])].filter(v => v > x))
    const text = fitText(g.text, stop - gap - x, px)
    return { x, text, full: g.text }
  })
}

// The box a label occupies: (x, y) is where SVG draws it (y = the baseline), `anchor` its
// text-anchor. Ascenders up to one font size above the baseline, descenders 0.3 below.
export function labelBox({ x, y, text, anchor = 'start', px = 11 }) {
  const w = textWidth(text, px)
  const x0 = anchor === 'end' ? x - w : anchor === 'middle' ? x - w / 2 : x
  return { x0, x1: x0 + w, y0: y - px, y1: y + px * 0.3 }
}

/**
 * A label that must not sit on anything already drawn: the first of `options`
 * ([{ x, y, text, anchor, px }], in order of preference) whose box stays inside `bounds`
 * ({ x0, x1, y0, y1 }) and clears every box in `obstacles`. null when none does -- the
 * caller then draws no label (its words stay in the tooltip and the card that explains it);
 * text is never shrunk to make it fit.
 */
export function placeLabel(options, obstacles = [], bounds = null) {
  for (const o of options) {
    const box = labelBox(o)
    if (bounds && (box.x0 < bounds.x0 || box.x1 > bounds.x1 || box.y0 < bounds.y0 || box.y1 > bounds.y1)) continue
    if (obstacles.some(b => boxesOverlap(box, b))) continue
    return { ...o, box }
  }
  return null
}

/**
 * Year ticks an axis can actually label: thinned to every 1, 2, 5, 10 or 20 years until
 * neighbours are `minGap` px apart, and dropped where they would touch a reserved label
 * (the hire date at the left, "Today" at the right). `ticks` = [{ label, x }] (x = left of the
 * centred label's anchor), `reserved` = [{ x0, x1 }].
 */
export function axisTicks(ticks, { minGap = 44, px = 10.5, reserved = [] } = {}) {
  const w = t => textWidth(t.label, px)
  let step = 1
  for (const s of [1, 2, 5, 10, 20, 50]) {
    step = s
    const kept = ticks.filter(t => Number(t.label) % s === 0)
    if (kept.every((t, i) => i === 0 || t.x - kept[i - 1].x >= Math.max(minGap, w(t) + 8))) break
  }
  return ticks.filter(t => Number(t.label) % step === 0).filter(t => {
    const a = t.x - w(t) / 2, b = t.x + w(t) / 2
    return reserved.every(r => b + 4 <= r.x0 || a - 4 >= r.x1)
  })
}

// Do two boxes {x0, x1, y0, y1} overlap? (the test of every layout above)
export function boxesOverlap(a, b) {
  return Math.min(a.x1, b.x1) - Math.max(a.x0, b.x0) > 0 && Math.min(a.y1, b.y1) - Math.max(a.y0, b.y0) > 0
}

// 1st, 2nd, 3rd, 4th ... 11th, 12th, 13th, 21st
export function ordinal(n) {
  const v = Math.round(Number(n))
  if (!Number.isFinite(v)) return '—'
  const t = v % 100, o = v % 10
  return `${v}${t >= 11 && t <= 13 ? 'th' : o === 1 ? 'st' : o === 2 ? 'nd' : o === 3 ? 'rd' : 'th'}`
}

// The peer comparison in words. Ties are named: a person paid exactly what most peers are
// paid (a pay scale) reads that way, not as a bare percentile.
export function peerSummary(r, n) {
  if (!r) return null
  const pct = `${ordinal(r.percentile)} percentile of peers`
  if (r.same == null) return pct
  if (r.same === n) return `All ${n} peers are paid this same rate (${pct}).`
  const parts = [r.below ? `${r.below} paid less` : null, r.same ? `${r.same} paid the same` : null,
                 r.above ? `${r.above} paid more` : null].filter(Boolean)
  return `${pct}: ${parts.join(', ')}.`
}

// The Onboarding line: one segment per hire whose onboarding is on record (a rehire may
// have several), from started to completed -- or to today while it is open. Read from the
// same parts the before-hire panel shows, so the line and the panel never disagree.
export function onboardingSegments(pre, today) {
  if (!pre?.available) return []
  const hires = pre.hires?.length ? pre.hires : [pre]
  const out = []
  for (const h of hires) {
    const o = h.onboarding?.available ? h.onboarding : null
    if (!o || dayMs(o.started_on) == null) continue
    out.push({ from: o.started_on, to: o.completed_on || today, open: !o.completed_on, hire_date: h.hire_date,
               label: `Onboarding · ${o.tasks_total != null ? `${o.tasks_done} of ${o.tasks_total} tasks done` : (o.status || 'started')}${o.completed_on ? ` · completed ${fmtDate(o.completed_on)}` : ''}` })
  }
  return out.sort((a, b) => dayMs(a.from) - dayMs(b.from))
}

// Why the Onboarding line is empty: the section's reason, else the latest hire's part's.
export function onboardingReason(section) {
  if (!section?.available) return section?.reason || 'not available'
  const hires = section.hires?.length ? section.hires : [section]
  return hires.map(h => h.onboarding?.reason).find(Boolean) || 'not available'
}

// ── guidance (the page's tooltips and its "How to read this" key) ──────────────────────

/**
 * Compare to peers, when it can't be drawn: a few words for beside the switch. The full
 * reason (the server's, unchanged) stays in the tooltip; anything unrecognised reads
 * "Not available" rather than a guess.
 */
export function peerShortReason(reason) {
  const r = String(reason || '')
  const few = r.match(/fewer than (\d+)/i)
  if (few) return `Fewer than ${few[1]} peers`
  if (/hourly rate/i.test(r)) return 'No hourly rate'
  if (/no current pay rate/i.test(r)) return 'No current pay rate'
  if (/pay basis/i.test(r)) return 'Pay basis not recorded'
  if (/no current job and work location/i.test(r)) return 'No job and location'
  if (/not been synced/i.test(r)) return 'Pay rates not synced'
  if (/could not be read/i.test(r)) return 'Not readable just now'
  return 'Not available'
}

/**
 * Where a tooltip goes: beside its anchor (never on it), wholly inside the viewport.
 * `a` = the anchor's client rect, `w`/`h` = the tooltip's size, `vw`/`vh` = the viewport,
 * `order` = the sides to try. The first side where it fits wins; when none fits, the side
 * with the most room, clamped to the viewport.
 */
export function placeTip(a, w, h, vw, vh, order = ['top', 'bottom', 'right', 'left'], { margin = 8, gap = 8 } = {}) {
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi))
  const cx = clamp(a.left + (a.right - a.left) / 2 - w / 2, margin, vw - margin - w)
  const cy = clamp(a.top + (a.bottom - a.top) / 2 - h / 2, margin, vh - margin - h)
  const at = {
    top: { side: 'top', x: cx, y: a.top - gap - h, room: a.top - margin },
    bottom: { side: 'bottom', x: cx, y: a.bottom + gap, room: vh - margin - a.bottom },
    right: { side: 'right', x: a.right + gap, y: cy, room: vw - margin - a.right },
    left: { side: 'left', x: a.left - gap - w, y: cy, room: a.left - margin },
  }
  const fits = p => p.x >= margin && p.y >= margin && p.x + w <= vw - margin && p.y + h <= vh - margin
  const hit = order.map(k => at[k]).find(fits)
  if (hit) return hit
  const best = Object.values(at).sort((p, q) => q.room - p.room)[0]
  return { ...best, x: clamp(best.x, margin, Math.max(margin, vw - margin - w)), y: clamp(best.y, margin, Math.max(margin, vh - margin - h)) }
}
